// Sync engine: pushes the durable outbox in FIFO order and pulls commit-ordered changes, applying them
// beneath pending local operations (rebase) atomically together with the cursor. One tab at a time holds the
// sync lock (Web Locks; server-side dedupe remains the final defense). Retries: exponential backoff + jitter.
import { reactive } from 'vue';
import type { IDBPDatabase, IDBPTransaction } from 'idb';
import { TransportError, type SyncTransport } from '@/app/ports';
import type { TaskPanesDB } from '@/db/localDb';
import type { ListRecord, ProjectRecord, TaskRecord } from './diff';
import type { Change, Operation, OperationResult, OutboxRecord } from './ops';
import { activityToLog, composeLog, mergeRaw, type ServerActivity } from './activity';
import { applyServerFile, type FileQueue, type ServerFile } from './files';
import type { FileQueueRecord } from '@/db/localDb';
import { listFromServer, projectFromServer, replayList, replayProject, replayTask, taskFromServer, type ServerList, type ServerProject, type ServerTask } from './records';
import { rankBetween } from './rank';

/** Cached user directory entry (project members seen in the pull feed); `username` since design v6 F3. */
export interface MetaUser { id: string; name: string; email: string; color: string; username?: string | null; /** v7 profile photo URL (access-checked server endpoint) */ photo?: string | null }

export type SyncState = 'saved_on_device' | 'pending' | 'syncing' | 'synced' | 'offline' | 'action_required';
export interface SyncStatus { state: SyncState; pending: number; lastSyncAt: number; detail: string; conflicts: number }
export const newSyncStatus = (): SyncStatus => ({ state: 'synced', pending: 0, lastSyncAt: 0, detail: '', conflicts: 0 });
/** What the sync indicator shows (with several workspaces loaded: the aggregate of their engines). */
export const syncStatus = reactive(newSyncStatus());

const FEED_VERSION = 2;

export interface EngineOptions {
  db: IDBPDatabase<TaskPanesDB>;
  transport: SyncTransport;
  workspaceId: string;
  deviceId: string;
  lockName: string;
  /** Called after server data was written locally (UI reloads its projection). */
  /** `from`: 'pull' = other people's / devices' data arrived; 'push' = only my own operations were acknowledged. */
  onApplied: (users: MetaUser[], from: 'push' | 'pull') => void;
  onAuthRequired: () => void;
  now?: () => number;
  random?: () => number;
  /** Presentation of date-only values in activity entries (calendar/locale aware). */
  formatDate?: (isoDate: string) => string;
  /** Offline attachment queue (P12); processed after operations are pushed. */
  files?: FileQueue;
  /** Signed-in user: replays my pending comment reactions over server data (v11, ADR 0018). */
  userId?: string;
  /** Where this engine reports its state (default: the global `syncStatus`). */
  status?: SyncStatus;
}

const BATCH = 100, PULL_LIMIT = 500, MAX_BACKOFF = 60_000;
const ALL: Array<'meta' | 'outbox' | 'projects' | 'lists' | 'tasks' | 'conflicts' | 'fileQueue'> = ['meta', 'outbox', 'projects', 'lists', 'tasks', 'conflicts', 'fileQueue'];
type Tx = IDBPTransaction<TaskPanesDB, typeof ALL, 'readwrite'>;

export class SyncEngine {
  private running = false;
  private rerun = false;
  private paused = false;
  private inflight: Promise<void> | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private debounce: ReturnType<typeof setTimeout> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly st: SyncStatus;
  constructor(private readonly o: EngineOptions) { this.st = o.status ?? syncStatus; }

  private now() { return (this.o.now || Date.now)(); }
  backoff(attempts: number): number { const cap = Math.min(MAX_BACKOFF, 1000 * 2 ** Math.max(0, attempts - 1)); return Math.round(cap * (0.5 + 0.5 * (this.o.random || Math.random)())); }

  start(pollMs = 15_000): void {
    this.timer = setInterval(() => void this.sync(), pollMs);
    window.addEventListener('online', this.onOnline);
    document.addEventListener('visibilitychange', this.onVisible);
    void this.sync();
  }
  stop(): void {
    if (this.timer) clearInterval(this.timer);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    window.removeEventListener('online', this.onOnline);
    document.removeEventListener('visibilitychange', this.onVisible);
  }
  private kick = () => { void this.sync(); };
  /** Connectivity returned: retry queued work now instead of waiting out the offline-era backoff. */
  private onOnline = () => { void this.clearBackoff().then(() => this.sync()); }; // an auth pause stays paused
  private onVisible = () => { if (document.visibilityState === 'visible') void this.sync(); };
  /** After a local commit: show "saved on device", sync shortly (debounced). */
  notifyLocalChange(): void {
    void this.refreshPending();
    if (this.debounce) clearTimeout(this.debounce);
    this.debounce = setTimeout(() => void this.sync(), 100); // short: other devices see the edit almost at once (ADR 0013)
  }
  resume(): void { this.paused = false; void this.sync(); }
  /**
   * Realtime says the workspace reached change `seq`. Already pulled (typically the echo of my own push, which the same
   * cycle pulled back) → nothing to do; while a cycle runs, one more only if it did not get that far.
   */
  remoteChanged(seq?: number): void {
    if (seq !== undefined && seq <= this.seenSeq) return;
    if (seq !== undefined && this.inflight) { this.awaitSeq = Math.max(this.awaitSeq, seq); return; }
    void this.sync();
  }
  /** Highest change sequence pulled in this session (reset by a fresh snapshot: a restore can lower sequences). */
  private seenSeq = 0;
  private awaitSeq = 0;

  async refreshPending(): Promise<number> {
    const n = (await this.o.db.countFromIndex('outbox', 'byState', 'pending')) + (await this.o.db.countFromIndex('outbox', 'byState', 'inflight')) + (this.o.files ? await this.o.files.pendingCount() : 0);
    this.st.pending = n;
    this.st.conflicts = (await this.o.db.getAll('conflicts')).filter(c => !c.resolved).length;
    if (n && this.st.state !== 'syncing' && this.st.state !== 'offline' && this.st.state !== 'action_required') this.st.state = 'saved_on_device';
    return n;
  }

  /** User-requested sync ("Sync now", retry): clears backoff of queued operations and files, then syncs. */
  async retryNow(): Promise<void> {
    await this.clearBackoff();
    this.paused = false;
    await this.sync();
  }
  /** Queued operations and files become due now (retry, reconnect). */
  private async clearBackoff(): Promise<void> {
    const tx = this.o.db.transaction(['outbox', 'fileQueue'], 'readwrite');
    for (const r of (await tx.objectStore('outbox').getAll()) as OutboxRecord[]) {
      if (r.state === 'pending' && r.next_attempt_at > 0) await tx.objectStore('outbox').put({ ...r, next_attempt_at: 0 });
    }
    for (const f of await tx.objectStore('fileQueue').getAll()) {
      if (f.state === 'queued' && f.next_attempt_at > 0) await tx.objectStore('fileQueue').put({ ...f, next_attempt_at: 0 });
    }
    await tx.done;
  }

  /**
   * One full cycle (push then pull), serialized within the tab and across tabs. Calls made while a cycle runs
   * request one more cycle and resolve only when that has finished too (so "Sync now" sees the final state).
   */
  sync(): Promise<void> {
    if (this.paused) return Promise.resolve();
    if (this.inflight) { this.rerun = true; return this.inflight; }
    this.running = true;
    this.inflight = (async () => {
      try {
        do {
          this.rerun = false;
          const locks = (navigator as Navigator & { locks?: LockManager }).locks;
          let ran = false;
          if (locks) await locks.request(this.o.lockName, { ifAvailable: true }, async lock => { if (lock) { ran = true; await this.cycle(); } });
          else { ran = true; await this.cycle(); }
          // realtime announced a change this cycle did not reach (only when it ran: another tab holding the lock pulls it)
          if (ran && this.awaitSeq > this.seenSeq) this.rerun = true;
          this.awaitSeq = 0;
        } while (this.rerun && !this.paused);
      } finally {
        this.running = false;
        this.inflight = null;
        if (this.awaitSeq > this.seenSeq) { this.awaitSeq = 0; queueMicrotask(() => void this.sync()); } // arrived as the cycle ended
      }
    })();
    return this.inflight;
  }

  private async cycle(): Promise<void> {
    // While offline, a retry attempt keeps showing "offline" until it actually reaches the server: flipping to
    // "syncing" on every attempt (each keystroke queues one) made the offline banner disappear and pop up again.
    // Only sending my own changes shows as "syncing"; routine pulls (polling, realtime) change nothing visible, so
    // the indicator and the screen do not churn every few seconds.
    // (count first: right after start the count is not known yet and unsent work must never read as "saved to cloud")
    const queued = await this.refreshPending();
    if (this.st.state !== 'offline' && queued > 0) this.st.state = 'syncing';
    try {
      for (let guard = 0; guard < 50; guard++) { if (!(await this.pushOnce())) break; }
      // Attachments after operations: an upload waits for its task's create to be acknowledged.
      if (this.o.files && await this.o.files.process(await this.o.db.getAll('outbox'))) this.o.onApplied(await this.users(), 'push');
      await this.pullAll();
      const at = this.now();
      if (Math.floor(at / 60_000) !== Math.floor(this.st.lastSyncAt / 60_000)) this.st.lastSyncAt = at; // shown to the minute
      const pending = await this.refreshPending();
      this.st.state = pending ? 'pending' : 'synced';
      this.st.detail = '';
    } catch (e) {
      const k = e instanceof TransportError ? e.kind : 'server';
      if (k === 'auth') { this.paused = true; this.st.state = 'action_required'; this.st.detail = 'Sign in again to sync'; this.o.onAuthRequired(); }
      else if (k === 'network') { this.st.state = 'offline'; this.st.detail = ''; }
      else { this.st.state = 'pending'; this.st.detail = (e as Error).message; }
      await this.refreshPending();
    }
    await this.scheduleRetry();
  }

  /** Wake up when the earliest backed-off operation becomes due (polling remains the safety net). */
  private async scheduleRetry(): Promise<void> {
    if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = null; }
    if (this.paused) return;
    const due = (await this.o.db.getAll('outbox')).filter(r => r.state === 'pending' || r.state === 'inflight').map(r => r.next_attempt_at);
    const fileDue = this.o.files ? await this.o.files.nextDue() : null;
    if (fileDue !== null) due.push(fileDue);
    if (!due.length) return;
    const wait = Math.max(250, Math.min(...due) - this.now());
    this.retryTimer = setTimeout(() => { this.retryTimer = null; void this.sync(); }, wait);
  }

  /** Pushes one batch; returns true when another batch may be ready. */
  async pushOnce(): Promise<boolean> {
    const now = this.now();
    const all = await this.o.db.getAll('outbox');
    // Strict FIFO: stop at the first operation still backing off after a transport failure, so later
    // operations can never overtake earlier ones (e.g. a task create before its project create).
    // Operations parked as dependency_pending may be skipped, together with later ops on the same entity.
    const batch: OutboxRecord[] = [], parked = new Set<string>();
    for (const r of all) {
      if (r.state !== 'pending' && r.state !== 'inflight') continue;
      if (parked.has(r.op.entity_id)) continue;
      if (r.next_attempt_at > now) {
        if (r.result?.status === 'dependency_pending') { parked.add(r.op.entity_id); continue; }
        break;
      }
      batch.push(r);
      if (batch.length >= BATCH) break;
    }
    if (!batch.length) return false;
    {
      const tx = this.o.db.transaction('outbox', 'readwrite');
      for (const r of batch) await tx.store.put({ ...r, state: 'inflight' });
      await tx.done;
    }
    let results: OperationResult[];
    try {
      results = await this.o.transport.push(this.o.workspaceId, this.o.deviceId, batch.map(r => r.op));
    } catch (e) {
      const auth = e instanceof TransportError && e.kind === 'auth';
      const tx = this.o.db.transaction('outbox', 'readwrite');
      for (const r of batch) await tx.store.put({ ...r, state: 'pending', attempts: auth ? r.attempts : r.attempts + 1, next_attempt_at: auth ? 0 : now + this.backoff(r.attempts + 1), last_error: (e as Error).message });
      await tx.done;
      throw e;
    }
    const byId = new Map(results.map(r => [r.operation_id, r]));
    const tx = this.o.db.transaction(ALL, 'readwrite');
    const outbox = tx.objectStore('outbox');
    let progressed = false;
    for (const rec of batch) {
      const res = byId.get(rec.op.operation_id);
      if (!res) { await outbox.put({ ...rec, state: 'pending' }); continue; }
      if (res.status === 'applied' || res.status === 'already_applied') {
        await outbox.delete(rec.seq!); progressed = true;
      } else if (res.status === 'dependency_pending') {
        await outbox.put({ ...rec, state: 'pending', attempts: rec.attempts + 1, next_attempt_at: now + this.backoff(rec.attempts + 1), result: res });
        continue;
      } else {
        // conflict / rejected: keep my value recoverable in the conflicts store, then drop the op
        await tx.objectStore('conflicts').put({ id: rec.op.operation_id, at: now, operation_id: rec.op.operation_id, entity_type: rec.op.entity_type, entity_id: rec.op.entity_id, local: { command: rec.op.command_type, payload: rec.op.payload, expected: rec.op.expected }, server: { status: res.status, conflict: res.conflict, error: res.error }, resolved: false });
        await outbox.delete(rec.seq!); progressed = true;
      }
      if (res.server_entity) await this.applyEntity(tx, rec.op.entity_type, res.server_entity);
    }
    await tx.done;
    if (progressed) this.o.onApplied(await this.users(), 'push');
    return progressed && batch.length === BATCH;
  }

  /** Pull pages until caught up; each page and its cursor are committed in one transaction. */
  async pullAll(): Promise<void> {
    let changed = false;
    // Local data format v2 (P13): task history comes from server activity. A device that synced before activity
    // was part of the feed takes one fresh snapshot (pending edits are rebased on top as usual) to receive it.
    if ((await this.o.db.get('meta', 'feed_version')) !== FEED_VERSION) {
      const tx = this.o.db.transaction('meta', 'readwrite');
      await tx.store.delete('cursor');
      await tx.store.put(FEED_VERSION, 'feed_version');
      await tx.done;
    }
    for (let guard = 0; guard < 1000; guard++) {
      const cursor = (await this.o.db.get('meta', 'cursor')) as string | undefined ?? null;
      let page;
      try { page = await this.o.transport.pull(this.o.workspaceId, cursor, PULL_LIMIT); }
      catch (e) { if (e instanceof TransportError && e.kind === 'cursor_expired') { await this.o.db.delete('meta', 'cursor'); continue; } throw e; }
      const tx = this.o.db.transaction(ALL, 'readwrite');
      if (page.mode === 'snapshot' && !cursor) { await this.markSnapshotStart(tx); this.seenSeq = 0; }
      for (const c of page.changes) { await this.applyChange(tx, c); if (c.seq > this.seenSeq) this.seenSeq = c.seq; }
      if (page.mode === 'snapshot' && !page.has_more && await this.sweepSnapshot(tx)) changed = true; // removals alone must refresh the view too
      // Pending resnapshots are persisted with the cursor so a crash cannot lose a newly granted project.
      if (page.resnapshot?.length) {
        const queued = new Set(((await tx.objectStore('meta').get('resnapshot')) as string[] | undefined) || []);
        for (const id of page.resnapshot) queued.add(id);
        await tx.objectStore('meta').put([...queued], 'resnapshot');
      }
      await tx.objectStore('meta').put(page.cursor, 'cursor');
      await tx.done;
      if (page.changes.length) changed = true;
      for (const projectId of ((await this.o.db.get('meta', 'resnapshot')) as string[] | undefined) || []) {
        try { await this.pullProject(projectId); } catch (e) { if (!(e instanceof TransportError && (e.status === 404 || e.kind === 'forbidden'))) throw e; } // access revoked again meanwhile
        const rest = (((await this.o.db.get('meta', 'resnapshot')) as string[] | undefined) || []).filter(x => x !== projectId);
        await this.o.db.put('meta', rest, 'resnapshot');
        changed = true;
      }
      if (!page.has_more) break;
    }
    if (changed) this.o.onApplied(await this.users(), 'pull');
  }

  /** Newly granted access: fetch a complete authorized snapshot of that project (main cursor unchanged). */
  async pullProject(projectId: string): Promise<void> {
    let cursor: string | null = null;
    for (let guard = 0; guard < 1000; guard++) {
      const page = await this.o.transport.pull(this.o.workspaceId, cursor, PULL_LIMIT, projectId);
      const tx = this.o.db.transaction(ALL, 'readwrite');
      for (const c of page.changes) await this.applyChange(tx, c);
      await tx.done;
      cursor = page.cursor;
      if (!page.has_more) break;
    }
  }

  private async users(): Promise<MetaUser[]> {
    return ((await this.o.db.get('meta', 'users')) as MetaUser[]) || [];
  }

  // ---- applying server state beneath pending ops ----
  /** My unsent operations for an entity, in outbox (FIFO) order. */
  private pendingFor(all: OutboxRecord[], entityId: string): Operation[] {
    return all.filter(r => r.op.entity_id === entityId && r.state !== 'rejected').map(r => r.op);
  }

  private snapshotSeen = new Set<string>();
  private async markSnapshotStart(_tx: unknown): Promise<void> { this.snapshotSeen = new Set(); }
  /** After a full snapshot, drop local entities the server no longer shows us (unless created locally and unsent). */
  /** @returns how many local entities the snapshot removed */
  private async sweepSnapshot(tx: Tx): Promise<number> {
    const pendingIds = new Set((await tx.objectStore('outbox').getAll()).map((r: OutboxRecord) => r.op.entity_id));
    let removed = 0;
    for (const store of ['projects', 'lists', 'tasks'] as const) {
      for (const key of await tx.objectStore(store).getAllKeys()) if (!this.snapshotSeen.has(String(key)) && !pendingIds.has(String(key))) { await tx.objectStore(store).delete(key); removed++; }
    }
    return removed;
  }

  async applyChange(tx: Tx, c: Change): Promise<void> {
    this.snapshotSeen.add(c.entity_id);
    if (c.entity_type === 'activity') { if (c.op === 'upsert' && c.data) await this.applyActivity(tx, c.data as unknown as ServerActivity); return; }
    if (c.entity_type === 'file') { await this.applyFile(tx, c); return; }
    if (c.entity_type !== 'project' && c.entity_type !== 'list' && c.entity_type !== 'task') return; // types this client does not store
    const store = c.entity_type === 'project' ? 'projects' : c.entity_type === 'list' ? 'lists' : 'tasks';
    if (c.op === 'delete' || c.op === 'revoke') {
      if (c.entity_type === 'project') {
        await tx.objectStore('projects').delete(c.entity_id);
        for (const l of await tx.objectStore('lists').index('byProject').getAll(c.entity_id)) await tx.objectStore('lists').delete(l.id);
        for (const t of await tx.objectStore('tasks').index('byProject').getAll(c.entity_id)) await tx.objectStore('tasks').delete(t.id);
      } else if (store !== 'projects') await tx.objectStore(store).delete(c.entity_id);
      return;
    }
    if (c.data) await this.applyEntity(tx, c.entity_type as 'project' | 'list' | 'task', c.data);
  }

  private async applyEntity(tx: Tx, type: string, data: Record<string, unknown>): Promise<void> {
    const outbox = (await tx.objectStore('outbox').getAll()) as OutboxRecord[];
    const id = String(data.id);
    if (type === 'project') {
      const d = data as unknown as ServerProject;
      const ps = tx.objectStore('projects');
      const existing = (await ps.get(id)) as ProjectRecord | undefined;
      let rank = existing?.rank;
      if (!rank) { const all = (await ps.getAll()) as ProjectRecord[]; const last = all.map(p => p.rank).sort().pop() || null; rank = rankBetween(last, null); }
      await ps.put(replayProject(projectFromServer(d, existing, rank), this.pendingFor(outbox, id), existing, this.o.userId));
      const users = new Map(((await tx.objectStore('meta').get('users')) as MetaUser[] || []).map(u => [u.id, u]));
      for (const m of d.members || []) users.set(m.id, { id: m.id, name: m.name, email: m.email || users.get(m.id)?.email || '', color: m.color || '#5b8def', username: m.username ?? users.get(m.id)?.username ?? null, photo: 'photo_url' in m ? m.photo_url ?? null : users.get(m.id)?.photo ?? null });
      await tx.objectStore('meta').put([...users.values()], 'users');
    } else if (type === 'list') {
      await tx.objectStore('lists').put(replayList(listFromServer(data as unknown as ServerList), this.pendingFor(outbox, id)));
    } else if (type === 'task') {
      const ts = tx.objectStore('tasks');
      const existing = (await ts.get(id)) as TaskRecord | undefined;
      const pending = this.pendingFor(outbox, id);
      const rec = replayTask(taskFromServer(data as unknown as ServerTask, existing), pending, existing, this.o.userId);
      if (existing && pending.some(o => o.command_type === 'moveTask')) rec.rank = existing.rank; // keep my provisional position
      rec.slog = existing?.slog;
      rec.log = composeLog(rec.slog, rec.log, pending.length > 0);
      await ts.put(rec);
    }
  }

  /** Server file metadata → chip on its task; a queued local removal wins until it is sent. */
  private async applyFile(tx: Tx, c: Change): Promise<void> {
    const ts = tx.objectStore('tasks');
    if (c.op === 'upsert' && c.data) {
      const f = c.data as unknown as ServerFile;
      const queued = (await tx.objectStore('fileQueue').get(f.id)) as FileQueueRecord | undefined;
      if (queued?.kind === 'delete') return;
      // v10.1: project comment images have no task — they sit on their project.
      if (!f.task_id) { const ps = tx.objectStore('projects'), pr = f.project_id ? ((await ps.get(f.project_id)) as ProjectRecord | undefined) : undefined; if (!pr) return; applyServerFile(pr, f, this.o.workspaceId); await ps.put(pr); return; }
      const task = (await ts.get(f.task_id)) as TaskRecord | undefined;
      if (!task) return;
      applyServerFile(task, f, this.o.workspaceId);
      await ts.put(task);
      return;
    }
    // delete/revoke: drop the chip wherever it is (ids only in tombstones)
    let cursor = await ts.openCursor();
    while (cursor) {
      const t = cursor.value as TaskRecord;
      if ((t.files || []).some(x => x.id === c.entity_id)) { await cursor.update({ ...t, files: t.files!.filter(x => x.id !== c.entity_id) }); break; }
      cursor = await cursor.continue();
    }
    let pc = await tx.objectStore('projects').openCursor();
    while (pc) {
      const p = pc.value as ProjectRecord;
      if ((p.files || []).some(x => x.id === c.entity_id)) { await pc.update({ ...p, files: p.files!.filter(x => x.id !== c.entity_id) }); break; }
      pc = await pc.continue();
    }
  }

  /** Server activity of a task → its log (idempotent by activity id). Activity of unknown tasks is ignored. */
  private async applyActivity(tx: Tx, a: ServerActivity): Promise<void> {
    if (!a.task_id) return;
    const ts = tx.objectStore('tasks');
    const task = (await ts.get(a.task_id)) as TaskRecord | undefined;
    if (!task) return;
    const after = (a.changes?.list_id as { after?: unknown } | undefined)?.after;
    const list = typeof after === 'string' ? ((await tx.objectStore('lists').get(after)) as ListRecord | undefined) : undefined;
    const users = new Map((((await tx.objectStore('meta').get('users')) as Array<{ id: string; name: string }> | undefined) || []).map(u => [u.id, u.name]));
    const entries = activityToLog(a, task, {
      date: this.o.formatDate || (iso => iso),
      listTitle: id => (list && list.id === id ? list.title : null),
      userName: id => users.get(id) || 'Someone',
    });
    if (!entries.length) return;
    const outbox = (await tx.objectStore('outbox').getAll()) as OutboxRecord[];
    task.slog = mergeRaw(task.slog, entries);
    task.log = composeLog(task.slog, task.log, this.pendingFor(outbox, task.id).length > 0);
    await ts.put(task);
  }
}

export type { ListRecord };
